import React, { useEffect, useState } from 'react';
import { CheckCircle2, Search, User, Mail, Zap, TrendingUp, Sparkles } from 'lucide-react';
import { subscriptionsApi } from '../../../api/subscriptions';
import { UnsubscribeModal } from '../UnsubscribeModal';

export interface Plan {
  id: number;
  name: string;
  display_name: string;
  daily_searches: number;
  monthly_dm_unlocks: number;
  monthly_email_verifications: number;
  dm_unlock_price_cents: number;
  email_verification_price_cents: number;
  price_cents: number;
  price_display: string;
  billing_interval: string | null;
  badge_type: 'MOST_POPULAR' | 'BEST_VALUE' | 'USAGE_BASED' | null;
  features: {
    allow_multiple_queued_searches: boolean;
    max_queued_searches: number;
    allow_topups: boolean;
    priority_support: boolean;
    dedicated_support: boolean;
    email_discovery: boolean;
    email_verification: boolean;
    confidence_badges: boolean;
  };
}

interface SubscriptionPlansProps {
  currentPlanName?: string;
  isTrial?: boolean;
  cancelAtPeriodEnd?: boolean;
  currentPeriodEnd?: string | null;
  onSelectPlan: (planId: number) => void;
  onUnsubscribeSuccess?: () => void;
  onRequestPlanSwitch?: (planId: number) => void;
}

export const PLAN_FEATURES: Array<{ label: string; getAvailable: (plan: Plan) => boolean }> = [
  { label: 'Decision maker contacts (names, roles & emails)', getAvailable: () => true },
  { label: 'Verified business emails (pay only for valid ones)', getAvailable: (plan) => plan.features.email_discovery },
  { label: 'Email confidence badges', getAvailable: (plan) => plan.features.confidence_badges },
  { label: 'Phone numbers & addresses', getAvailable: () => true },
  { label: 'Google ratings & reviews data', getAvailable: () => true },
  { label: 'Social media profiles', getAvailable: () => true },
  { label: 'Export to CSV/Excel', getAvailable: () => true },
  // { label: 'Buy extra unlocks & verifications', getAvailable: () => true },
  { label: 'Priority support', getAvailable: (plan) => plan.features.priority_support },
];

export const PARENT_TIER: Record<string, string> = {
  growth: 'starter',
  scale: 'growth',
};

export const PARENT_DISPLAY_NAME: Record<string, string> = {
  starter: 'Starter',
  growth: 'Growth',
};

export function getActiveSearchLabel(plan: Plan): string {
  const max = plan.features.max_queued_searches;
  switch (max) {
    case 1:
      return '1 active search';
    case 2:
      return 'Up to 2 active searches';
    case 3:
      return 'Up to 3 active searches';
    default:
      return 'Unlimited searches';
  }
}

interface FeatureItemProps {
  plan: Plan;
  feature: string;
  available: boolean;
  highlighted?: boolean;
}

const FeatureItem: React.FC<FeatureItemProps> = ({ feature, available, highlighted = false }) => {
  if (!available) return null;

  return (
    <div className="flex items-start gap-2">
      <CheckCircle2 className={`mt-0.5 h-4 w-4 shrink-0 sm:h-5 sm:w-5 ${highlighted ? 'text-purple-500' : 'text-green-500'}`} />
      <span className={`text-[11px] leading-snug sm:text-xs md:text-sm ${highlighted ? 'font-semibold text-purple-700' : 'text-gray-700'}`}>
        {feature}
      </span>
    </div>
  );
};

export const SubscriptionPlans: React.FC<SubscriptionPlansProps> = ({
  currentPlanName,
  isTrial,
  cancelAtPeriodEnd,
  currentPeriodEnd,
  onSelectPlan,
  onUnsubscribeSuccess,
  onRequestPlanSwitch,
}) => {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [showUnsubscribeModal, setShowUnsubscribeModal] = useState(false);
  const [unsubscribing, setUnsubscribing] = useState(false);

  useEffect(() => {
    loadPlans();
  }, []);

  const loadPlans = async () => {
    try {
      const response = await subscriptionsApi.getPlans();
      setPlans(response.plans);
    } catch (error) {
      console.error('Failed to load plans:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUnsubscribe = async (reason: string, otherReasonText?: string) => {
    setUnsubscribing(true);
    try {
      await subscriptionsApi.unsubscribe(reason, otherReasonText);
      setShowUnsubscribeModal(false);
      if (onUnsubscribeSuccess) {
        onUnsubscribeSuccess();
      }
    } catch (error) {
      console.error('Failed to unsubscribe:', error);
      alert('Failed to unsubscribe. Please try again.');
    } finally {
      setUnsubscribing(false);
    }
  };

  const isPlanCurrent = (planName: string) => planName === currentPlanName;
  const hasLiveSubscription = !!currentPlanName && currentPlanName !== 'discover';

  const getPlanButtonText = (plan: Plan) => {
    if (isPlanCurrent(plan.name)) {
      if (plan.name === 'discover') {
        return 'Current Plan';
      }
      if (isTrial) {
        return 'Current plan · trial';
      }
      if (cancelAtPeriodEnd) {
        return 'Cancellation scheduled';
      }
      return 'Unsubscribe';
    }
    if (plan.name === 'discover') return 'Select Plan';
    if (hasLiveSubscription && onRequestPlanSwitch) {
      return `Switch to ${plan.display_name}`;
    }
    return 'Select Plan';
  };

  const getPlanButtonClass = (plan: Plan) => {
    if (isPlanCurrent(plan.name)) {
      if (plan.name === 'discover') {
        return 'bg-green-600 text-white cursor-not-allowed';
      }
      if (isTrial || cancelAtPeriodEnd) {
        return 'bg-gray-200 text-gray-400 cursor-not-allowed';
      }
      return 'bg-red-600 hover:bg-red-700 text-white transition-colors';
    }
    if (plan.name === 'discover') {
      return 'bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white transition-colors';
    }
    return 'bg-primary hover:bg-primary-hover text-white transition-colors';
  };

  const handleButtonClick = (plan: Plan) => {
    if (isPlanCurrent(plan.name)) {
      if (plan.name !== 'discover' && !isTrial && !cancelAtPeriodEnd) {
        setShowUnsubscribeModal(true);
      }
      return;
    }
    if (hasLiveSubscription && plan.name !== 'discover' && onRequestPlanSwitch) {
      onRequestPlanSwitch(plan.id);
    } else {
      onSelectPlan(plan.id);
    }
  };


  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  const sortedPlans = [...plans].sort((a, b) => {
    const order = ['starter', 'growth', 'scale', 'discover'];
    return order.indexOf(a.name) - order.indexOf(b.name);
  });

  const filteredPlans = sortedPlans.filter(plan => {
    if (plan.name === 'discover' && hasLiveSubscription) {
      return false;
    }
    return true;
  });

  return (
    <div className="flex justify-center px-1 sm:px-0">
      <div className="grid max-w-6xl grid-cols-1 items-stretch gap-3 sm:gap-4 md:grid-cols-2 md:gap-5 lg:gap-6 xl:grid-cols-3">
        {filteredPlans.map((plan) => {
        const isGrowth = plan.name === 'growth';
        const isScale = plan.name === 'scale';
        const isCurrent = isPlanCurrent(plan.name);
        
        const cardContent = (
          <div
            className={`flex h-full max-h-full w-full flex-col overflow-hidden rounded-[10px] p-3 transition-all sm:p-4 lg:p-6 ${
              isCurrent
                ? 'bg-green-50'
                : 'bg-white'
            }`}
          >
          <div className="mb-2 flex flex-col gap-2 sm:mb-4">
            {isPlanCurrent(plan.name) && (
              <div className="inline-flex items-center self-start rounded-full bg-green-600 px-2 py-1 text-xs font-bold text-white sm:px-3 sm:py-1.5">
                Current plan
              </div>
            )}
            {plan.badge_type === 'USAGE_BASED' && (
              <div className="inline-flex items-center self-start rounded-full bg-gradient-to-r from-yellow-500 to-orange-500 px-2 py-1 text-xs font-semibold text-white sm:px-3 sm:py-1">
                Trial plan
              </div>
            )}
            {!isPlanCurrent(plan.name) && plan.badge_type === 'MOST_POPULAR' && (
              <div className="inline-flex items-center self-start rounded-full bg-gradient-to-r from-purple-600 to-pink-600 px-2 py-1 text-xs font-semibold text-white sm:px-3 sm:py-1">
                Most popular
              </div>
            )}
            {!isPlanCurrent(plan.name) && plan.badge_type === 'BEST_VALUE' && (
              <div className="inline-flex items-center self-start rounded-full bg-gradient-to-r from-purple-600 to-pink-600 px-2 py-1 text-xs font-semibold text-white sm:px-3 sm:py-1">
                Best value
              </div>
            )}
          </div>

          <h3 className="mb-2 min-w-0 break-words text-xl font-bold text-primary sm:text-2xl">
            {plan.display_name}
          </h3>

          <div className="mb-3 sm:mb-4">
            <div className="text-2xl font-bold text-secondary sm:text-3xl md:text-4xl">
              {plan.price_display}
            </div>
            {plan.billing_interval && (
              <div className="text-xs sm:text-sm text-gray-600">per {plan.billing_interval}</div>
            )}
          </div>

          <div className="mb-4">
            <div className="text-sm font-semibold text-gray-700 mb-1.5">Usage limits</div>
            <div className="space-y-1.5">
              <div className="flex min-w-0 items-start gap-2">
                <Search className="mt-0.5 h-4 w-4 shrink-0 text-blue-600" />
                <span className="min-w-0 text-sm font-semibold text-secondary sm:text-base">
                  {plan.daily_searches} {plan.daily_searches === 1 ? 'search' : 'searches'} {plan.name === 'discover' ? 'total' : '/ day'}
                </span>
              </div>
              <div className="flex min-w-0 items-start gap-2">
                <User className="mt-0.5 h-4 w-4 shrink-0 text-purple-600" />
                <span className="min-w-0 text-sm font-semibold leading-snug text-secondary sm:text-base">
                  {plan.monthly_dm_unlocks.toLocaleString()}{' '}
                  {plan.monthly_dm_unlocks === 1 ? 'people enrichment' : 'people enrichments'}{' '}
                  {plan.name === 'discover' ? 'total' : '/ month'}
                </span>
              </div>
              <div className="flex min-w-0 items-start gap-2">
                <Mail className="mt-0.5 h-4 w-4 shrink-0 text-green-600" />
                <span className="min-w-0 text-sm font-semibold leading-snug text-secondary sm:text-base">
                  {plan.monthly_email_verifications.toLocaleString()} email {plan.monthly_email_verifications === 1 ? 'verification' : 'verifications'} {plan.name === 'discover' ? 'total' : '/ month'}
                </span>
              </div>
              <div className="flex min-w-0 items-start gap-2">
                <Zap className="mt-0.5 h-4 w-4 shrink-0 text-yellow-500" />
                <span className="min-w-0 text-sm font-semibold text-secondary sm:text-base">
                  {getActiveSearchLabel(plan)}
                </span>
              </div>
            </div>
          </div>

          <div className="border-t border-gray-200 my-4" />

          <div className="mb-3 flex-grow overflow-hidden sm:mb-6">
            <div className="mb-1 text-sm font-semibold text-gray-700">Includes</div>
            {(() => {
              const parentTierName = PARENT_TIER[plan.name];
              const parentPlan = parentTierName ? sortedPlans.find(p => p.name === parentTierName) : null;

              if (parentPlan) {
                const newFeatures = PLAN_FEATURES.filter(
                  (item) => item.getAvailable(plan) && !item.getAvailable(parentPlan)
                );

                return (
                  <div className="space-y-1.5 sm:space-y-2.5">
                    <div className="flex items-center gap-2.5">
                      <CheckCircle2 className="h-4 w-4 shrink-0 text-green-600 sm:h-5 sm:w-5" />
                      <span className="text-xs font-semibold text-green-700 sm:text-sm">
                        All from {PARENT_DISPLAY_NAME[parentTierName]}
                      </span>
                    </div>
                    <div className="flex items-center gap-2.5">
                      <TrendingUp className="h-4 w-4 shrink-0 text-blue-600 sm:h-5 sm:w-5" />
                      <span className="text-xs font-semibold text-blue-700 sm:text-sm">
                        {isScale ? '2x' : '3x'} higher usage than {PARENT_DISPLAY_NAME[parentTierName]}
                      </span>
                    </div>
                    {newFeatures.length > 0 && (
                      <div className="space-y-1.5 pt-1 sm:space-y-2">
                        <div className="flex items-center gap-1.5">
                          <Sparkles className="h-3.5 w-3.5 shrink-0 text-purple-500" />
                          <span className="text-[10px] font-semibold uppercase tracking-wide text-purple-600 sm:text-xs">New in {plan.display_name}</span>
                        </div>
                        {newFeatures.map((item) => (
                          <FeatureItem key={item.label} plan={plan} feature={item.label} available={true} highlighted={true} />
                        ))}
                      </div>
                    )}
                  </div>
                );
              }

              return (
                <div className="space-y-1.5 sm:space-y-2.5">
                  {PLAN_FEATURES.filter((item) => item.getAvailable(plan)).map((item) => (
                    <FeatureItem key={item.label} plan={plan} feature={item.label} available={true} />
                  ))}
                </div>
              );
            })()}
          </div>

          <button
            onClick={() => handleButtonClick(plan)}
            disabled={isPlanCurrent(plan.name) && (plan.name === 'discover' || isTrial || !!cancelAtPeriodEnd)}
            className={`w-full min-h-[44px] rounded-lg px-3 py-2.5 text-sm font-semibold sm:py-3 sm:text-base ${getPlanButtonClass(plan)}`}
          >
            {getPlanButtonText(plan)}
          </button>
          </div>
        );
        
        if (isCurrent) {
          return (
            <div key={plan.id} className={`rounded-xl border-2 border-green-500 shadow-xl ring-2 ring-green-200 flex flex-col h-full ${plan.name === 'discover' ? 'xl:col-start-3' : ''}`}>
              {cardContent}
            </div>
          );
        }

        if (isGrowth) {
          return (
            <div
              key={plan.id}
              className="relative flex h-full flex-col rounded-xl shadow-xl"
              style={{
                padding: '3px',
                background: 'linear-gradient(to bottom right, rgb(168, 85, 247), rgb(236, 72, 153), rgb(147, 51, 234))',
              }}
            >
              {cardContent}
            </div>
          );
        }

        if (plan.name === 'discover') {
          return (
            <div key={plan.id} className="rounded-xl border-2 border-gray-300 shadow-lg flex flex-col h-full xl:col-start-3">
              {cardContent}
            </div>
          );
        }

        return (
          <div key={plan.id} className="rounded-xl border-2 border-gray-300 shadow-lg flex flex-col h-full">
            {cardContent}
          </div>
        );
        })}

        <UnsubscribeModal
          isOpen={showUnsubscribeModal}
          onClose={() => setShowUnsubscribeModal(false)}
          onConfirm={handleUnsubscribe}
          isLoading={unsubscribing}
          currentPeriodEnd={currentPeriodEnd}
        />
      </div>
    </div>
  );
};

